import React from "react";
import { MiniInfoPill, ThinButton } from "./CompactControls";

export default function BulkUploadSection({
    handleDownloadSample,
    handleDownloadBulkExcel,
    handleBulkFileChange,
    csvErrors,
    bulkItems,
    bulkLoading,
    handleSafeBulkSubmit,
}) {
    return (
        <div className="space-y-3">
            <div className="rounded-lg border bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-bold text-gray-800">
                                Bulk CSV Upload
                            </p>

                            <MiniInfoPill tone="success">
                                itemName + price required
                            </MiniInfoPill>

                            <MiniInfoPill>
                                photo must be URL
                            </MiniInfoPill>

                            <MiniInfoPill>
                                iconDish: Y / N
                            </MiniInfoPill>
                        </div>

                        <p className="mt-1 text-[11px] text-gray-400">
                            Download sample, fill it in Excel, export as CSV, then upload.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <ThinButton onClick={handleDownloadSample}>
                            Sample CSV
                        </ThinButton>

                        <ThinButton variant="blue" onClick={handleDownloadBulkExcel}>
                            Size Excel
                        </ThinButton>
                    </div>
                </div>

                {/* RULES BOX - kept in code, but compact */}
                <details className="mt-3 rounded-lg border border-green-200 bg-green-50">
                    <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-green-800">
                        CSV Columns & Rules
                    </summary>

                    <div className="border-t border-green-100 px-3 py-2 text-xs text-gray-700">
                        <div className="grid grid-cols-1 gap-x-6 gap-y-1 md:grid-cols-2">
                            <p>
                                <span className="font-semibold text-red-500">itemName</span> – Item name required.
                            </p>

                            <p>
                                <span className="font-semibold text-red-500">itemDescription</span> – Short description.
                            </p>

                            <p>
                                <span className="font-semibold text-red-500">photo</span> – Full image URL, e.g. https://...
                            </p>

                            <p>
                                <span className="font-semibold text-red-500">category</span> – Category name, e.g. Food, Grocery.
                            </p>

                            <p>
                                <span className="font-semibold text-red-500">price</span> – MRP / base price.
                            </p>

                            <p>
                                <span className="font-semibold text-red-500">discountPrice</span> – Final selling price.
                                <span className="ml-1 text-gray-500">
                                    Discount auto-calculated as price - discountPrice.
                                </span>
                            </p>

                            <p>
                                <span className="font-semibold text-red-500">variantName</span> – Optional.
                            </p>

                            <p>
                                <span className="font-semibold text-red-500">iconDish</span> – Y = Veg, N = Non-Veg.
                            </p>

                            <p>
                                <span className="font-semibold text-red-500">rackNumber</span> – e.g. 1, 2A.
                            </p>

                            <p>
                                <span className="font-semibold text-red-500">expiryDate</span> – format YYYY/MM/DD.
                            </p>
                        </div>

                        <p className="mt-2 border-t border-green-100 pt-2 text-[11px] text-gray-500">
                            Other fields like status, ratings, subCategory, etc. are auto-handled.
                            <span className="ml-1 font-semibold text-red-500">customizations</span>{" "}
                            is always sent as empty array [].
                        </p>
                    </div>
                </details>

                <div className="mt-3 flex flex-wrap items-end gap-2">
                    <div className="min-w-[260px] flex-1">
                        <label className="mb-0.5 block text-[11px] font-semibold text-gray-500">
                            Upload filled CSV
                        </label>

                        <input
                            type="file"
                            accept=".csv,text/csv,application/vnd.ms-excel"
                            className="h-8 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                            onChange={handleBulkFileChange}
                        />
                    </div>

                    {bulkItems.length > 0 && (
                        <ThinButton
                            variant="primary"
                            disabled={
                                bulkLoading ||
                                csvErrors.length > 0 ||
                                bulkItems.length === 0
                            }
                            onClick={handleSafeBulkSubmit}
                        >
                            {bulkLoading ? "Uploading..." : `Upload ${bulkItems.length}`}
                        </ThinButton>
                    )}
                </div>
            </div>

            {csvErrors.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                    <p className="mb-1 font-bold">
                        CSV Issues ({csvErrors.length})
                    </p>

                    <ul className="max-h-24 list-disc space-y-1 overflow-auto pl-5">
                        {csvErrors.map((err, index) => (
                            <li key={index}>{err}</li>
                        ))}
                    </ul>
                </div>
            )}

            {bulkItems.length > 0 && (
                <div className="overflow-hidden rounded-lg border bg-white">
                    <div className="flex items-center justify-between gap-3 border-b bg-gray-50 px-3 py-2">
                        <div>
                            <p className="text-xs font-bold text-gray-800">
                                Parsed {bulkItems.length} items
                            </p>
                            <p className="text-[11px] text-gray-400">
                                Preview before upload
                            </p>
                        </div>

                        <ThinButton
                            variant="primary"
                            disabled={
                                bulkLoading ||
                                csvErrors.length > 0 ||
                                bulkItems.length === 0
                            }
                            onClick={handleSafeBulkSubmit}
                        >
                            {bulkLoading ? "Uploading..." : "Upload Items"}
                        </ThinButton>
                    </div>

                    <div className="max-h-[calc(100vh-300px)] w-full overflow-auto">
                        <table className="min-w-[980px] w-full text-xs">
                            <thead className="sticky top-0 z-10 bg-green-50 text-left text-gray-700">
                                <tr>
                                    <th className="border-b px-2 py-1.5">#</th>
                                    <th className="border-b px-2 py-1.5">Item Name</th>
                                    <th className="border-b px-2 py-1.5">Category</th>
                                    <th className="border-b px-2 py-1.5">Price</th>
                                    <th className="border-b px-2 py-1.5">Discount</th>
                                    <th className="border-b px-2 py-1.5">Icon</th>
                                    <th className="border-b px-2 py-1.5">Rack</th>
                                    <th className="border-b px-2 py-1.5">Expiry</th>
                                </tr>
                            </thead>

                            <tbody>
                                {bulkItems.map((item, i) => (
                                    <tr key={i} className="border-t hover:bg-gray-50">
                                        <td className="px-2 py-1.5">{i + 1}</td>

                                        <td
                                            className="max-w-[220px] truncate px-2 py-1.5"
                                            title={item.itemName}
                                        >
                                            {item.itemName}
                                        </td>

                                        <td className="px-2 py-1.5">
                                            {item.category}
                                        </td>

                                        <td className="px-2 py-1.5">
                                            {item.price}
                                        </td>

                                        <td className="px-2 py-1.5">
                                            {item.discount}
                                        </td>

                                        <td className="px-2 py-1.5">
                                            {item.iconDish}
                                        </td>

                                        <td className="px-2 py-1.5">
                                            {item.rackNumber}
                                        </td>

                                        <td className="px-2 py-1.5">
                                            {item.expiryDate}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}